import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { KeycloakService, KeycloakEventType } from 'keycloak-angular';
import { AuthApiService } from '@angular-workspace/services';
import { ToasterService } from './toaster.service';
import { GoogleAnalyticsService } from './google-analytics.service';
import { filter } from 'rxjs/operators';

/**
 * Service to listen to Keycloak events and call user API after successful login
 */
@Injectable({
  providedIn: 'root',
})
export class AuthListenerService {
  private keycloak = inject(KeycloakService);
  private authApiService = inject(AuthApiService);
  private toasterService = inject(ToasterService);
  private router = inject(Router);
  private googleAnalytics = inject(GoogleAnalyticsService);
  private logoutMessageShown = false;

  /**
   * Initialize the listener for Keycloak authentication events
   */
  initialize(): void {
    // Check for logout flag on initialization
    this.checkLogoutFlag();

    // Listen to Keycloak events
    this.keycloak.keycloakEvents$.subscribe({
      next: async (event) => {
        if (event.type === KeycloakEventType.OnAuthSuccess) {
          // Track sign-in for GA4 (Sign In / day report)
          this.googleAnalytics.trackSignIn('Keycloak');
          // Call user API after successful authentication
          try {
            const userData = await this.authApiService.getUserInfo();
            localStorage.setItem('userData', JSON.stringify(userData));
          } catch {
            // User info fetch failed; app continues without stored userData
          }
        } else if (event.type === KeycloakEventType.OnAuthLogout) {
          // Clear user data on logout
          localStorage.removeItem('userData');
        }
      },
    });

    // Listen to route changes to check logout flag and clear form data
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.checkLogoutFlag();
        this.clearFormDataIfNeeded(event.urlAfterRedirects || event.url);
      });
  }

  /**
   * Clear form data from localStorage when user navigates away from contribution routes
   */
  private clearFormDataIfNeeded(currentUrl: string): void {
    const DATASET_FORM_DATA_KEY = 'datasetFormData';
    const MODEL_FORM_DATA_KEY = 'modelFormData';
    
    // Define routes that are part of the add-dataset flow (both org-admin and provider)
    const addDatasetRoutes = [
      // Org Admin routes
      '/org-admin/add-dataset',
      '/org-admin/add-dataset/metadata',
      '/org-admin/add-dataset/upload-metadata',
      '/org-admin/add-dataset/preview-metadata',
      '/org-admin/add-dataset/additionalInfo',
      '/org-admin/add-dataset/uploads',
      // Provider routes
      '/provider/add-contributions/add-dataset',
      '/provider/add-contributions/add-dataset/metadata',
      '/provider/add-contributions/add-dataset/upload-metadata',
      '/provider/add-contributions/add-dataset/preview-metadata',
      '/provider/add-contributions/add-dataset/additionalInfo',
      '/provider/add-contributions/add-dataset/uploads'
    ];
    
    // Define routes that are part of the add-model flow (both org-admin and provider)
    const addModelRoutes = [
      // Org Admin routes
      '/org-admin/add-model',
      '/org-admin/add-model/metadata',
      '/org-admin/add-model/upload-metadata',
      '/org-admin/add-model/preview-metadata',
      '/org-admin/add-model/additionalInfo',
      '/org-admin/add-model/uploads',
      // Provider routes
      '/provider/add-contributions/add-model',
      '/provider/add-contributions/add-model/metadata',
      '/provider/add-contributions/add-model/upload-metadata',
      '/provider/add-contributions/add-model/preview-metadata',
      '/provider/add-contributions/add-model/additionalInfo',
      '/provider/add-contributions/add-model/uploads'
    ];
    
    // Check if current route is part of any add-dataset flow
    const isInAddDatasetFlow = addDatasetRoutes.some(route => 
      currentUrl === route || currentUrl.startsWith(`${route}/`)
    );
    
    // Check if current route is part of any add-model flow
    const isInAddModelFlow = addModelRoutes.some(route => 
      currentUrl === route || currentUrl.startsWith(`${route}/`)
    );
    
    // If not in any add-dataset flow, clear datasetFormData
    if (!isInAddDatasetFlow && localStorage.getItem(DATASET_FORM_DATA_KEY)) {
      localStorage.removeItem(DATASET_FORM_DATA_KEY);
    }
    if (!isInAddModelFlow && localStorage.getItem(MODEL_FORM_DATA_KEY)) {
      localStorage.removeItem(MODEL_FORM_DATA_KEY);
    }
  }

  /**
   * Check if user has logged out and show appropriate message
   */
  private checkLogoutFlag(): void {
    if (this.logoutMessageShown) {
      return;
    }

    const logoutFlag = sessionStorage.getItem('logout-redirect');
    if (logoutFlag) {
      // Clear the flag
      sessionStorage.removeItem('logout-redirect');
      
      // Show logout message
      this.toasterService.info("You've successfully logged out", 3000);
      this.logoutMessageShown = true;
      
      // Reset flag after some time
      setTimeout(() => {
        this.logoutMessageShown = false;
      }, 5000);
    }
  }
}

